import { exploration as output, pipeline } from '@gosupersimple/types';
import { ZodError } from 'zod';

import { isUserQuestionMessage, restoreInvalidCell } from '@/core/cell';

import { ConversationMessageRole, ConversationMessageType } from '@/graphql';

import { exhaustiveCheck } from '@/lib/exhaustive';

import { Cell, ChatExploration, ChatMessage, Exploration, Pipeline, ChatSource } from './types';
import { restoreInvalidOperation } from './utils';

export const restoreInvalidOperations = (pipeline: Pipeline): Pipeline => {
  return {
    ...pipeline,
    operations: pipeline.operations.map(restoreInvalidOperation),
  };
};

export const exportViewCell = (cell: Cell): output.ViewCell => {
  switch (cell.kind) {
    case 'records':
      return {
        id: cell.id,
        kind: cell.kind,
        title: cell.title ?? undefined,
        excludeProperties: cell.excludeProperties ?? undefined,
        pipeline: pipeline.pipelineWithInstances.parse(restoreInvalidOperations(cell.pipeline)),
        visualisations: cell.visualisations,
        sort: cell.sort,
        viewOptions: cell.viewOptions ?? undefined,
      };
    case 'funnel':
      return {
        id: cell.id,
        kind: cell.kind,
        title: cell.title ?? undefined,
        pipeline: pipeline.basePipeline.parse(restoreInvalidOperations(cell.pipeline)),
        viewOptions: cell.viewOptions ?? undefined,
      };
    case 'cohort':
      return {
        id: cell.id,
        kind: 'cohort',
        title: cell.title ?? undefined,
        pipeline: pipeline.pipelineWithInstances.parse(restoreInvalidOperations(cell.pipeline)),
        viewOptions: cell.viewOptions ?? undefined,
      };
    case 'variable':
      return {
        id: cell.id,
        kind: cell.kind,
        definition: cell.definition,
        viewOptions: cell.viewOptions ?? undefined,
      };
    case 'sql':
      return {
        id: cell.id,
        kind: 'sql',
        title: cell.title ?? undefined,
        pipeline: pipeline.basePipeline.parse(restoreInvalidOperations(cell.pipeline)),
        visualisations: cell.visualisations,
        viewOptions: cell.viewOptions ?? undefined,
      };
    case 'python':
      return {
        id: cell.id,
        kind: 'python',
        title: cell.title ?? undefined,
        pipeline: pipeline.basePipeline.parse(restoreInvalidOperations(cell.pipeline)),
        viewOptions: cell.viewOptions ?? undefined,
      };
    case 'text':
      return {
        id: cell.id,
        kind: cell.kind,
        title: cell.title ?? undefined,
        content: cell.content ?? undefined,
        viewOptions: cell.viewOptions ?? undefined,
      };
    case 'chat':
      return {
        id: cell.id,
        kind: cell.kind,
        messages: cell.messages
          .filter((m) => m.type !== 'progress')
          .map((message) => ({
            ...message,
            enabledSources: (isUserQuestionMessage(message) ? message.sources : []) ?? [],
          })),
        conversationId: cell.conversationId,
        viewOptions: cell.viewOptions ?? undefined,
      };
    case 'conversation':
      return cell;
    case 'invalid':
      return exportViewCell(restoreInvalidCell(cell));
    default:
      exhaustiveCheck(cell);
  }
};

export const exportExploration = (exploration: Exploration): output.Exploration => {
  try {
    return {
      explorationId: exploration.explorationId,
      description: exploration.description ?? undefined,
      labels: exploration.labels,
      name: exploration.name,
      parameters: exploration.parameters,
      view: {
        cells: exploration.view.cells.map(exportViewCell),
        canvas: exploration.view.canvas,
        options: exploration.view.options,
        tabs: exploration.view.tabs,
      },
    };
  } catch (e) {
    if ((e as ZodError).name === 'ZodError') {
      throw new Error(`Unexpected exploration format`);
    }
    throw e;
  }
};

const chatMessageRoleToGql = (role: ChatMessage['role']): ConversationMessageRole => {
  switch (role) {
    case 'user':
      return ConversationMessageRole.User;
    case 'assistant':
      return ConversationMessageRole.Assistant;
    default:
      exhaustiveCheck(role);
  }
};

const chatMessageTypeToGql = (type: ChatMessage['type']): ConversationMessageType => {
  switch (type) {
    case 'clarifying_answer':
      return ConversationMessageType.ClarifyingAnswer;
    case 'initial_user_prompt':
      return ConversationMessageType.InitialUserPrompt;
    case 'followup_question':
      return ConversationMessageType.FollowupQuestion;
    case 'clarifying_question':
      return ConversationMessageType.ClarifyingQuestion;
    case 'progress':
      return ConversationMessageType.Progress;
    default:
      exhaustiveCheck(type);
  }
};

export const exportChatMessage = (message: ChatMessage | ChatExploration) => {
  if (message.type === 'exploration') {
    return {
      id: message.id,
      type: ConversationMessageType.Exploration,
      role: chatMessageRoleToGql(message.role),
      exploration: message.exploration,
    };
  }

  if (message.role === 'assistant') {
    return {
      id: message.id,
      message: message.message,
      type: chatMessageTypeToGql(message.type),
      role: chatMessageRoleToGql(message.role),
    };
  }

  if (isUserQuestionMessage(message)) {
    return {
      id: message.id,
      message: message.message,
      enabledSources: message.sources ?? [ChatSource.Database],
      type: chatMessageTypeToGql(message.type),
      research: message.research ?? false,
      role: chatMessageRoleToGql(message.role),
    };
  }

  return {
    id: message.id,
    message: message.message,
    type: chatMessageTypeToGql(message.type),
    role: chatMessageRoleToGql(message.role),
  };
};
